import React, { useContext, useEffect, useState } from 'react';
import { Box, Typography, CircularProgress } from '@material-ui/core';
import './gallery.less';

import { IPhoto } from 'core/context/consumer/objectDetails';
import { getPhotoUrl } from 'core/helpers/file-size.helper';
import { FileSize } from 'core/enums';

import NavigateBeforeIcon from '@material-ui/icons/NavigateBefore';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';

import { PhotoLightbox } from './PhotoLightbox';
import { UserContext } from '../../../../core/context/user';
import { makeStyles } from '@material-ui/core/styles';
import { ConfirmDeleteModal } from '../../../_others';
import { ObjectApi } from '../../../../core/api/consumer';
import { CropPhotoModal } from '../../../../scenes/consumer/objectDetails/components/CropPhotoModal';
import { UtilsContext } from '../../../../core/context/utils.context';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { CollectorApi } from '../../../../core/api/consumer';

type GalleryProps = {
    photos: IPhoto[];
    isMyObject?: boolean;
    displayDeleteButton?: boolean;
    artifactId?: number;
    maxHeight?: number;
    onPhotoDeleteSuccess?: (id: number) => void;
};

const useStyles = makeStyles({
    remove: {
        width: 15,
        height: 17,
        fontSize: 12,
        cursor: 'pointer',
        marginRight: 25,
    },
    crop: {
        width: 15,
        height: 17,
        fontSize: 12,
        cursor: 'pointer',
        marginRight: 10,
    },
    actionsBox: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
});

const reorder = (list: IPhoto[], startIndex: number, endIndex: number): IPhoto[] => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
};

export const Gallery: React.FC<GalleryProps> = ({
    photos,
    isMyObject,
    displayDeleteButton,
    artifactId,
    maxHeight,
    onPhotoDeleteSuccess,
}) => {
    const classes = useStyles();

    const primaryPhoto = photos?.length ? photos.find((p: IPhoto) => p.isPrimary) : null;
    const userContext = useContext(UserContext);
    const utilsContext = useContext(UtilsContext);

    const [currentPhoto, setCurrentPhoto] = useState<IPhoto | null>(primaryPhoto || null);
    const [carouselWidth, setCarouselWidth] = useState(0);
    const [lightbox, setLightbox] = useState<number>(-1);
    const [displayConfirmDeleteModal, toggleConfirmDeleteModal] = useState(-1);
    const [photoOnCrop, setPhotoOnCrop] = useState<IPhoto | null>(null);
    const [displayPhotos, setDisplayPhotos] = useState<IPhoto[]>([]);
    const [isReordering, setIsReordering] = useState(false);

    useEffect(() => {
        const currentPhotoContainer = document.getElementById('current-photo-container');
        setCarouselWidth(currentPhotoContainer?.clientWidth || 0);
        function handleWindowResize(): void {
            const currentPhotoContainer = document.getElementById('current-photo-container');
            setCarouselWidth(currentPhotoContainer?.clientWidth || 0);
        }
        window.addEventListener('resize', handleWindowResize);
    }, []);

    useEffect(() => {
        const filteredPhotos = photos.filter((p) => p.deleted !== true && !p.wasDeleted);
        setDisplayPhotos(filteredPhotos);
    }, [photos]);

    useEffect(() => {
        let primaryPhoto = displayPhotos?.length
            ? displayPhotos.find((p: IPhoto) => p.isPrimary)
            : null;
        if (!primaryPhoto && displayPhotos?.length > 0) {
            // eslint-disable-next-line prefer-destructuring
            primaryPhoto = displayPhotos[0];
        }
        setCurrentPhoto(primaryPhoto || null);
    }, [displayPhotos]);

    const prevPhoto = (photo: IPhoto | null): void => {
        const currentPhotoIndex = displayPhotos.findIndex((p: IPhoto) => p.id === photo?.id);
        if (currentPhotoIndex === 0) {
            return;
        }

        setCurrentPhoto(displayPhotos[currentPhotoIndex - 1]);
        scrollCarousel(-50);
    };

    const nextPhoto = (photo: IPhoto | null): void => {
        const currentPhotoIndex = displayPhotos.findIndex((p: IPhoto) => p.id === photo?.id);
        if (currentPhotoIndex === displayPhotos.length - 1) {
            return;
        }
        setCurrentPhoto(displayPhotos[currentPhotoIndex + 1]);
        scrollCarousel(50);
    };

    const scrollCarousel = (x: number): void => {
        const carousel = document.getElementById('carousel');
        if (!carousel) {
            return;
        }
        carousel.scrollBy(x, 0);
    };

    const selectCurrentPhoto = (photo: IPhoto): void => {
        setCurrentPhoto(photo);
        const currentPhotoIndex = displayPhotos.findIndex((p: IPhoto) => p.id === photo.id);

        if (currentPhotoIndex === displayPhotos.length - 1) {
            scrollCarousel(100);
            return;
        }

        if (currentPhotoIndex === 0) {
            scrollCarousel(-100);
            return;
        }
        if (currentPhotoIndex < displayPhotos.length / 2) {
            scrollCarousel(-50);
            return;
        }
        scrollCarousel(50);
    };

    const onDragEnd = (result: any): void => {
        // dropped outside the list or no change
        if (!result.destination || result.source.index === result.destination.index) {
            return;
        }

        // Prevent drag during reordering
        if (isReordering) {
            return;
        }

        const reorderedPhotos = reorder(displayPhotos, result.source.index, result.destination.index);
        
        // Update local state immediately for instant feedback
        setDisplayPhotos(reorderedPhotos);

        // Save the new order to the server
        setIsReordering(true);
        const payload = reorderedPhotos.map((photo: IPhoto, index: number) => {
            return {
                id: photo.id,
                order: index,
            };
        });

        CollectorApi.reorderPhotos(payload)
            .then(() => {
                setIsReordering(false);
            })
            .catch((error: any) => {
                console.error(error.response);
                setIsReordering(false);
                // Revert to original order on error
                setDisplayPhotos(photos.filter((p) => p.deleted !== true && !p.wasDeleted));
            });
    };

    const onRemove = (photoId: number): void => {
        if (!artifactId) {
            return;
        }
        toggleConfirmDeleteModal(photoId);
    };

    const onCrop = (photo: IPhoto): void => {
        setPhotoOnCrop(photo);
    };

    const onPhotoDelete = (photoId: number): void => {
        if (!artifactId) {
            return;
        }

        ObjectApi.deletePhoto([photoId])
            .then(() => {
                const photo = displayPhotos.find((p) => p.id === photoId);
                if (photo) {
                    photo.wasDeleted = true;
                    setDisplayPhotos(displayPhotos.filter((p) => p.id !== photoId));
                }
                toggleConfirmDeleteModal(-1);
                setPhotoOnCrop(null);
                if (onPhotoDeleteSuccess) {
                    onPhotoDeleteSuccess(photoId);
                }
            })
            .catch((error: any) => {
                console.error(error.response);
                toggleConfirmDeleteModal(-1);
                utilsContext.addSnackbar('error');
            });
    };

    return (
        <Box className="artifact-gallery">
            <Box
                className="current-photo"
                id="current-photo-container"
                style={
                    userContext.lightTheme
                        ? { backgroundColor: '#EFEFEF', maxHeight: maxHeight ? maxHeight : 'unset' }
                        : { maxHeight: maxHeight ? maxHeight : 'unset' }
                }
            >
                {currentPhoto && (
                    <>
                        <img
                            src={getPhotoUrl(currentPhoto?.url, FileSize.MD) || ''}
                            alt={''}
                            onClick={() => setLightbox(photos.findIndex((p: IPhoto) => p.id === currentPhoto?.id))}
                            style={maxHeight ? { cursor: 'move', maxHeight } : { cursor: 'move' }}
                        />
                    </>
                )}
                {!currentPhoto && <Typography className="no-photo-text">There are no photos</Typography>}
            </Box>
            {photos?.length > 0 && (
                <>
                    {isMyObject && displayPhotos.length > 1 ? (
                        <Box position="relative">
                            {isReordering && (
                                <Box
                                    position="absolute"
                                    top={0}
                                    left={0}
                                    right={0}
                                    bottom={0}
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    bgcolor={userContext.lightTheme ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.5)'}
                                    zIndex={1000}
                                    style={{ borderRadius: '4px' }}
                                >
                                    <CircularProgress 
                                        size={24} 
                                        style={{ 
                                            color: userContext.lightTheme ? '#FE6502' : '#029BFE' 
                                        }} 
                                    />
                                </Box>
                            )}
                            <DragDropContext onDragEnd={onDragEnd}>
                                <Droppable droppableId="photo-carousel" direction="vertical" isDropDisabled={isReordering}>
                                    {(provided: any) => (
                                        <Box
                                            className="carousel"
                                            id="carousel"
                                            ref={provided.innerRef}
                                            {...provided.droppableProps}
                                            style={{
                                                ...(maxHeight ? { maxHeight } : {}),
                                                opacity: isReordering ? 0.6 : 1,
                                                pointerEvents: isReordering ? 'none' : 'auto',
                                            }}
                                        >
                                        {displayPhotos.length > 1 && (
                                            <Box className="left-arrow" onClick={() => prevPhoto(currentPhoto)}>
                                                <NavigateBeforeIcon />
                                            </Box>
                                        )}
                                        {displayPhotos.map((photo: IPhoto, index: number) => (
                                            <Draggable 
                                                key={photo.id} 
                                                draggableId={photo.id.toString()} 
                                                index={index}
                                                isDragDisabled={isReordering}
                                            >
                                                {(provided: any, snapshot: any) => (
                                                    <Box
                                                        id={`${photo.id}_carousel`}
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        className={`carousel-img
                                                             ${currentPhoto?.id === photo.id && !isMyObject ? 'selected-photo' : ''}
                                                             ${isMyObject ? 'thumbnail-action' : ''}
                                                             ${snapshot.isDragging ? 'dragging' : ''}`}
                                                        style={{
                                                            ...provided.draggableProps.style,
                                                        }}
                                                    >
                                                        <img
                                                            src={getPhotoUrl(photo.url, FileSize.SM) || ''}
                                                            alt={''}
                                                            {...(isReordering ? {} : provided.dragHandleProps)}
                                                            onClick={(e) => {
                                                                // Only select if not dragging and not reordering
                                                                e.stopPropagation();
                                                                if (!snapshot.isDragging && !isReordering) {
                                                                    selectCurrentPhoto(photo);
                                                                }
                                                            }}
                                                            style={
                                                                photo?.isPrimary
                                                                    ? { 
                                                                        border: '2px solid #d04c19', 
                                                                        cursor: isReordering ? 'not-allowed' : 'grab' 
                                                                    }
                                                                    : { cursor: isReordering ? 'not-allowed' : 'grab' }
                                                            }
                                                            className={`${currentPhoto?.id === photo.id && isMyObject ? 'selected-photo' : ''}`}
                                                        />
                                                        {isMyObject && (
                                                            <Box
                                                                className={`${classes.actionsBox} thumbnail-action-btn`}
                                                                style={!displayDeleteButton ? { justifyContent: 'center' } : {}}
                                                                onClick={(e) => e.stopPropagation()}
                                                                onMouseDown={(e) => e.stopPropagation()}
                                                            >
                                                                {
                                                                    <Box className={classes.crop} onClick={() => onCrop(photo)}>
                                                                        Edit
                                                                    </Box>
                                                                }
                                                                {displayDeleteButton && (
                                                                    <Box className={classes.remove} onClick={() => onRemove(photo.id)}>
                                                                        Remove
                                                                    </Box>
                                                                )}
                                                            </Box>
                                                        )}
                                                    </Box>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                        {displayPhotos.length > 1 && (
                                            <Box
                                                className="right-arrow"
                                                style={{ left: `${carouselWidth + 11}px` }}
                                                onClick={() => nextPhoto(currentPhoto)}
                                            >
                                                <NavigateNextIcon />
                                            </Box>
                                        )}
                                        </Box>
                                    )}
                                </Droppable>
                            </DragDropContext>
                        </Box>
                    ) : (
                        <Box className="carousel" id="carousel" style={maxHeight ? { maxHeight } : {}}>
                            {displayPhotos.length > 1 && (
                                <Box className="left-arrow" onClick={() => prevPhoto(currentPhoto)}>
                                    <NavigateBeforeIcon />
                                </Box>
                            )}
                            {displayPhotos.map((photo: IPhoto) => (
                                <Box
                                    id={`${photo.id}_carousel`}
                                    key={photo.id}
                                    className={`carousel-img
                                         ${currentPhoto?.id === photo.id && !isMyObject ? 'selected-photo' : ''}
                                         ${isMyObject ? 'thumbnail-action' : ''}`}
                                >
                                    <img
                                        src={getPhotoUrl(photo.url, FileSize.SM) || ''}
                                        alt={''}
                                        onClick={() => selectCurrentPhoto(photo)}
                                        style={
                                            photo?.isPrimary
                                                ? { border: '2px solid #d04c19', cursor: 'pointer' }
                                                : { cursor: 'pointer' }
                                        }
                                        className={`${currentPhoto?.id === photo.id && isMyObject ? 'selected-photo' : ''}`}
                                    />
                                    {isMyObject && (
                                        <Box
                                            className={`${classes.actionsBox} thumbnail-action-btn`}
                                            style={!displayDeleteButton ? { justifyContent: 'center' } : {}}
                                        >
                                            {
                                                <Box className={classes.crop} onClick={() => onCrop(photo)}>
                                                    Edit
                                                </Box>
                                            }
                                            {displayDeleteButton && (
                                                <Box className={classes.remove} onClick={() => onRemove(photo.id)}>
                                                    Remove
                                                </Box>
                                            )}
                                        </Box>
                                    )}
                                </Box>
                            ))}
                            {displayPhotos.length > 1 && (
                                <Box
                                    className="right-arrow"
                                    style={{ left: `${carouselWidth + 11}px` }}
                                    onClick={() => nextPhoto(currentPhoto)}
                                >
                                    <NavigateNextIcon />
                                </Box>
                            )}
                        </Box>
                    )}
                </>
            )}

            <ConfirmDeleteModal
                open={displayConfirmDeleteModal !== -1}
                isLoading={false}
                subtitle={'Are you sure you want to delete this photo?'}
                handleClose={() => toggleConfirmDeleteModal(-1)}
                onConfirm={() => onPhotoDelete(displayConfirmDeleteModal)}
            />

            {photoOnCrop && artifactId && (
                <CropPhotoModal
                    artifactId={artifactId}
                    open={true}
                    handleClose={() => setPhotoOnCrop(null)}
                    photo={photoOnCrop}
                />
            )}

            <PhotoLightbox
                open={lightbox !== -1}
                index={lightbox}
                photos={displayPhotos || []}
                handleClose={() => setLightbox(-1)}
            />
        </Box>
    );
};
